import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Phone, Mail, ChevronRight, Image as ImageIcon, ChevronDown, CheckCircle2, ShieldCheck, ArrowRight, MapPin, Clock, Award, Star, ExternalLink } from "lucide-react";
import { fetchPageById, fetchGalleryById } from "../lib/cms";
import { getOptimizedImageUrl } from "../lib/utils";
import { COMPANY_INFO, PAGES } from "../data";
import ContactForm from "../components/ContactForm";
import Lightbox from "../components/Lightbox";

export default function ServiceDetail({ forceSlug }) {
  const params = useParams();
  const rawSlug = forceSlug || params.slug;
  // Handle friendly alias
  const slug =
    rawSlug === "totalrenovering"
      ? "totalrenoveringer"
      : rawSlug === "totalentreprise"
      ? "totalentrepriser"
      : rawSlug === "ombygning"
      ? "ombygninger"
      : rawSlug;
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);

  useEffect(() => {
    async function loadService() {
      setLoading(true);
      const pageData = await fetchPageById(slug);
      if (!pageData) {
        // If not a valid service page, redirect to 404
        navigate("/404", { replace: true });
        return;
      }
      setPage(pageData);

      if (pageData.gallery_slug) {
        const galleryData = await fetchGalleryById(pageData.gallery_slug);
        setGallery(galleryData);
      } else {
        setGallery(null);
      }
      setLoading(false);
    }
    loadService();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  if (!page) return null;

  const title = page.title?.da || "";
  const contentHtml = page.content?.da || "";
  const metaTitle = page.meta_title?.da || page.meta_title || `${title} - Sehested Tømrerfirma ApS`;
  const metaDesc = page.meta_description?.da || page.meta_description || "";
  // Local fallback metadata & FAQ if available (e.g. for rich structured pages)
  const localData = PAGES[slug] || {};
  const ogImage = page.og_image?.da || page.og_image || localData.og_image || "";
  const faqData = page.faq || localData.faq || null;
  const schemaMarkup = page.schema_markup || localData.schema_markup || null;

  const isThreeTargetPages =
    slug === "totalrenoveringer" ||
    slug === "totalrenovering" ||
    slug === "ombygninger" ||
    slug === "ombygning" ||
    slug === "totalentrepriser" ||
    slug === "totalentreprise";

  return (
    <div className="bg-primary-50/30 py-10 md:py-16">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        {schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(schemaMarkup)}
          </script>
        )}
      </Helmet>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb & Navigation */}
        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-primary-500 mb-6 sm:mb-8">
          <Link to="/" className="hover:text-brand-600 transition-colors">Hjem</Link>
          <ChevronRight className="h-3.5 w-3.5 text-primary-400" />
          <Link to="/ydelser" className="hover:text-brand-600 transition-colors">Ydelser</Link>
          <ChevronRight className="h-3.5 w-3.5 text-primary-400" />
          <span className="text-primary-900 font-medium">{title}</span>
        </div>

        {/* Top Service Hero Banner */}
        <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-primary-950 via-primary-900 to-primary-950 p-6 sm:p-8 lg:p-10 text-white shadow-md border border-primary-800">
          <div className="relative z-10 max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-400/30 bg-brand-500/10 px-3.5 py-1 text-xs font-semibold text-brand-300 mb-3">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Dækket af Byg Garanti • Over 20 års erfaring</span>
            </div>

            <p className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl leading-tight">
              {slug === "totalrenoveringer" || slug === "totalrenovering"
                ? "Totalrenovering"
                : slug === "ombygninger" || slug === "ombygning"
                ? "Ombygning"
                : slug === "totalentrepriser" || slug === "totalentreprise"
                ? "Totalentreprise"
                : title}
            </p>

            <p className="mt-3 text-sm sm:text-base leading-relaxed text-primary-200">
              {slug === "totalrenoveringer" || slug === "totalrenovering"
                ? "Solidt tømrerarbejde med samme tømrermester fra første samtale til færdig bolig. Fast kontakt, gennemtænkte løsninger og ingen fordyrende mellemled."
                : slug === "ombygninger" || slug === "ombygning"
                ? "Gennemtænkt ombygning af hus i Aarhus og omegn. Samme erfarne tømrer fra start til slut, god dialog og løsninger tilpasset din bolig."
                : slug === "totalentrepriser" || slug === "totalentreprise"
                ? "Totalentreprise i Aarhus med én fast tømrer hele vejen. Tryg styring, tæt dialog og solidt håndværk tilpasset dit byggeprojekt."
                : `Professionel udførelse af ${title.toLowerCase()} med fokus på solidt håndværk, god dialog og holdbare resultater.`}
            </p>

            {/* Top Action CTAs */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#kontakt-sektion"
                className="inline-flex items-center justify-center rounded-full bg-brand-400 px-6 py-3.5 text-sm font-bold text-primary-950 transition-colors duration-200 hover:bg-brand-300 shadow-sm"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/20 hover:border-white/30 backdrop-blur-xs"
              >
                <Phone className="h-4 w-4 text-brand-300" />
                <span>Ring {COMPANY_INFO.phoneDisplay}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 items-start">
          {/* Main Content Column */}
          <div className="lg:col-span-8 space-y-6">
            {/* Rich Text Editorial Content */}
            <div className="bg-white rounded-2xl border border-primary-200/80 p-6 md:p-8 shadow-xs">
              {slug === "totalrenoveringer" || slug === "totalrenovering" ? (
                <div className="space-y-5 text-primary-800">
                  <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-primary-950 leading-tight">
                    Totalrenovering af hus i Hasselager og Aarhus med en erfaren tømrer
                  </h1>

                  <p className="text-base sm:text-lg leading-relaxed text-primary-700">
                    Overvejer du en totalrenovering af hus, hjælper jeg med at samle tømrerarbejdet i en løsning, der passer til boligen og dine ønsker. Jeg hedder Søren Sehested, driver{" "}
                    <Link
                      to="/"
                      className="font-semibold text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                    >
                      Sehested Tømrerfirma
                    </Link>
                    , har over 20 års erfaring og står selv for kontakten og det praktiske arbejde fra første samtale til den færdige tømreropgave.
                  </p>

                  <div className="flex flex-wrap items-center gap-2.5">
                    <a
                      href="tel:24263012"
                      className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                    >
                      <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                      Ring 24 26 30 12
                    </a>
                    <a
                      href="mailto:ssj@ssehested.dk"
                      className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                    >
                      <Mail className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                      Send mail
                    </a>
                    <a
                      href="#kontakt-sektion"
                      className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2 text-xs font-bold text-white transition-colors shadow-xs"
                    >
                      Få et tilbud
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </a>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Hvornår giver en totalrenovering mest værdi?
                    </h2>
                    <p className="mt-3 leading-relaxed">
                      En totalrenovering giver især mening, når flere dele af boligen skal fornyes på samme tid, eller når gamle løsninger ikke længere passer til hverdagen. I stedet for at behandle hvert rum som en løs opgave ser jeg på sammenhængen mellem indretning, materialer og det nødvendige tømrerarbejde.
                    </p>
                    <p className="mt-3 leading-relaxed">
                      Totalrenovering af hus kan både handle om udtryk og funktion. Målet kan være bedre rumudnyttelse, nye{" "}
                      <Link
                        to="/ydelser/doere-og-vinduer"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        døre og vinduer
                      </Link>
                      , arbejde på{" "}
                      <Link
                        to="/ydelser/tagarbejde"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        taget
                      </Link>{" "}
                      eller en større{" "}
                      <Link
                        to="/ombygning"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        ombygning
                      </Link>
                      . Jeg tager udgangspunkt i det eksisterende hus, så løsningen bliver tilpasset boligen frem for en standardmodel.
                    </p>
                    <ul className="mt-4 space-y-2 list-disc pl-5 text-primary-700">
                      <li>Ombygning af rum og en mere velfungerende indretning</li>
                      <li>Udskiftning af døre og vinduer med præcis tilpasning</li>
                      <li>Tagarbejde som en del af den samlede renovering</li>
                      <li>Tømrerarbejde der binder boligens løsninger sammen</li>
                    </ul>
                  </div>

                  {/* Mid-content CTA block 1 */}
                  <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-base font-bold text-primary-950">
                        Har du et konkret hus eller projekt i tankerne?
                      </p>
                      <p className="mt-1 text-xs sm:text-sm text-primary-700">
                        Få en grundig gennemgang af mulighederne og et uforpligtende tilbud.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <a
                        href="#kontakt-sektion"
                        className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2.5 text-xs font-bold text-white transition-colors shadow-xs"
                      >
                        Få et tilbud
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                      <a
                        href={`tel:${COMPANY_INFO.phoneRaw}`}
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-3.5 py-2.5 text-xs font-semibold text-primary-900 transition-colors"
                      >
                        <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Ring 24 26 30 12
                      </a>
                    </div>
                  </div>

                  {/* Showcase image for totalrenovering */}
                  <figure className="my-6 overflow-hidden rounded-2xl border border-primary-200/80 bg-white shadow-xs">
                    <img
                      src={getOptimizedImageUrl(
                        "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00005-scaled.jpeg",
                        { width: 1100, quality: 82 }
                      )}
                      alt="Totalrenovering af bolig udført af Sehested Tømrerfirma i Hasselager og Aarhus"
                      className="h-64 sm:h-80 md:h-96 w-full object-cover"
                      loading="lazy"
                    />
                    <figcaption className="px-4 py-3 text-xs sm:text-sm text-primary-600 bg-primary-50/60 border-t border-primary-100 flex items-center justify-between">
                      <span>Totalrenovering med fokus på præcist tømrerarbejde og holdbare materialer.</span>
                      <span className="font-semibold text-brand-700 shrink-0 ml-3">Sehested Tømrerfirma</span>
                    </figcaption>
                  </figure>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Tømrer til totalrenovering, derfor gør fast kontakt en forskel
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Når du vælger mig som tømrer til totalrenovering, er det også mig, du taler med undervejs, og mig der står med værktøjet. Det giver en direkte vej fra aftale til udførelse og færre overdragelser, hvor detaljer ellers kan gå tabt eller blive opfattet forskelligt.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Jeg har over 20 års erfaring med tømrerarbejde og arbejder med både små opgaver og omfattende renoveringsopgaver. Min rolle er at holde fokus på det aftalte tømrerarbejde, detaljerne og den praktiske udførelse, så du ved, hvem du skal spørge gennem forløbet.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Totalrenovering og{" "}
                      <Link
                        to="/totalentreprise"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        totalentreprise
                      </Link>{" "}
                      betyder ikke det samme. En totalrenovering beskriver, at store dele af boligen bliver fornyet, mens totalentreprise er en entrepriseform. Hvis projektet rummer flere typer arbejde, bør ansvar, omfang og aftaler derfor være tydelige, inden udførelsen begynder.
                    </p>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Totalrenovering i Aarhus, Højbjerg og Viby med lokal tømrer
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Med base i Hasselager hjælper jeg med totalrenovering i Aarhus og Aarhus Syd. Det gælder også totalrenovering i{" "}
                      <Link
                        to="/toemrer-hoejbjerg"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Højbjerg
                      </Link>{" "}
                      og{" "}
                      <Link
                        to="/toemrer-viby"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Viby
                      </Link>
                      . Mit arbejdsområde omfatter desuden{" "}
                      <Link
                        to="/toemrer-maarslet"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Mårslet
                      </Link>
                      ,{" "}
                      <Link
                        to="/toemrer-skanderborg"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Skanderborg
                      </Link>{" "}
                      og{" "}
                      <Link
                        to="/toemrer-odder"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Odder
                      </Link>
                      , så du kan vælge en lokalt forankret tømrer til den større renovering.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Den lokale placering er kun en del af oplevelsen. Den vigtigste forskel er, at kontakten ikke bliver sendt videre mellem forskellige personer. Jeg følger selv tømreropgaven fra start til slut og kan løbende forholde mig til de forhold, der viser sig i den eksisterende bolig.
                    </p>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Hvad koster en totalrenovering af hus?
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Der findes ikke én fast pris på totalrenovering af hus, som passer til alle boliger. Prisen afhænger blandt andet af husets størrelse og stand, hvor meget der skal ændres, og hvilke materialer der vælges. Tag, vinduer og større indgreb i indretningen kan ændre både omfang og økonomi.
                    </p>

                    <h3 className="font-display text-lg font-bold text-primary-950 mt-4">
                      Totalrenovering af hus, pris og valg der påvirker budgettet
                    </h3>
                    <p className="mt-2 leading-relaxed">
                      Når du undersøger pris på en totalrenovering, er en løs pris pr. kvadratmeter sjældent nok til at vurdere dit projekt. To lige store huse kan kræve meget forskelligt arbejde. Et realistisk budget kræver derfor, at opgaven bliver afgrænset ud fra de konkrete forhold.
                    </p>
                    <ul className="mt-3 space-y-1.5 list-disc pl-5 text-primary-700">
                      <li>Boligens størrelse og nuværende stand</li>
                      <li>Omfanget af ombygning og tømrerarbejde</li>
                      <li>Valg af materialer, døre, vinduer og tagløsning</li>
                      <li>Adgangsforhold og skjulte forhold i konstruktionen</li>
                    </ul>

                    <h3 className="font-display text-lg font-bold text-primary-950 mt-4">
                      Sådan får du et brugbart tilbud på totalrenovering
                    </h3>
                    <p className="mt-2 leading-relaxed">
                      For at få et brugbart tilbud på totalrenovering er det en hjælp at beskrive boligens størrelse, nuværende stand og dine vigtigste ønsker. Tegninger og billeder kan gøre behovet tydeligere, men den endelige vurdering bør tage afsæt i selve opgaven og de løsninger, du ønsker.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Du kan altid kontakte mig direkte på tlf.{" "}
                      <a
                        href="tel:24263012"
                        className="font-bold text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        24 26 30 12
                      </a>{" "}
                      eller mail{" "}
                      <a
                        href="mailto:ssj@ssehested.dk"
                        className="font-bold text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        ssj@ssehested.dk
                      </a>
                      , hvis du vil have en faglig vurdering af dit projekt.
                    </p>
                  </div>

                  {/* Mid-content CTA block 2 */}
                  <div className="rounded-xl border border-primary-200 bg-primary-50/80 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-base font-bold text-primary-950">
                        Klar til at give boligen et nyt udgangspunkt?
                      </p>
                      <p className="mt-0.5 text-xs sm:text-sm text-primary-700">
                        Ring direkte på 24 26 30 12 eller udfyld kontaktformularen.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <a
                        href="tel:24263012"
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-100/70 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                      >
                        <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Ring 24 26 30 12
                      </a>
                      <a
                        href="#kontakt-sektion"
                        className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2 text-xs font-bold text-white transition-colors shadow-xs"
                      >
                        Få et tilbud
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              ) : slug === "ombygninger" || slug === "ombygning" ? (
                <div className="space-y-5 text-primary-800">
                  <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-primary-950 leading-tight">
                    Ombygning af hus i Hasselager & Aarhus med samme tømrer fra start til slut
                  </h1>

                  <p className="text-base sm:text-lg leading-relaxed text-primary-700">
                    Drømmer du om en bolig, der passer bedre til din hverdag, hjælper jeg med ombygning af hus i Aarhus, Højbjerg, Viby og resten af Aarhus Syd. Jeg hedder Søren Sehested, driver{" "}
                    <Link
                      to="/"
                      className="font-semibold text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                    >
                      Sehested Tømrerfirma
                    </Link>{" "}
                    og har over 20 års erfaring. Du taler med mig om løsningen, og jeg udfører selv tømrerarbejdet med blik for helhed og detaljer.
                  </p>

                  <div className="flex flex-wrap items-center gap-2.5">
                    <a
                      href="tel:24263012"
                      className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                    >
                      <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                      Ring 24 26 30 12
                    </a>
                    <a
                      href="mailto:ssj@ssehested.dk"
                      className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                    >
                      <Mail className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                      Send mail
                    </a>
                    <a
                      href="#kontakt-sektion"
                      className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2 text-xs font-bold text-white transition-colors shadow-xs"
                    >
                      Få et tilbud
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </a>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Hvad kan den rigtige ombygning gøre for dit hjem?
                    </h2>
                    <p className="mt-3 leading-relaxed">
                      En ombygning ændrer mere end boligens udtryk. Den kan skabe en bedre rumfordeling, samle køkken og opholdsrum eller give plads til et ekstra værelse. Før arbejdet begynder, ser jeg på dine ønsker og de eksisterende forhold, så løsningen bliver tilpasset huset og den måde, du bruger det på.
                    </p>
                    <p className="mt-3 leading-relaxed">
                      Forskellen på renovering og ombygning ligger ofte i formålet. Ved renovering fornyer man typisk det eksisterende, mens ombygninger ændrer funktion, indretning eller konstruktion. I mange projekter hænger{" "}
                      <Link
                        to="/totalrenovering"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        renovering
                      </Link>{" "}
                      og ombygning naturligt sammen, fordi nye rum også kræver nye overflader, døre, gulve eller vinduer.
                    </p>
                    <p className="mt-3 leading-relaxed">
                      Omfanget kan være alt fra en enkelt ny skillevæg til en større ændring af boligen. Som tømrer kan jeg blandt andet hjælpe med de bygningsdele, der binder projektet sammen. Vi afklarer opgaven ud fra huset, materialerne og dine ønsker, før den praktiske løsning bliver lagt fast.
                    </p>
                    <ul className="mt-4 space-y-2 list-disc pl-5 text-primary-700">
                      <li>Ny rumfordeling med vægge, lofter og døråbninger</li>
                      <li>Ombygning af køkken og tilstødende opholdsrum</li>
                      <li>Tilpasning af døre, vinduer, gulve og træværk</li>
                      <li>Ændringer ved tag og andre bærende trækonstruktioner</li>
                    </ul>
                  </div>

                  {/* Mid-content CTA block 1 */}
                  <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-base font-bold text-primary-950">
                        Overvejer du at bygge om i boligen?
                      </p>
                      <p className="mt-1 text-xs sm:text-sm text-primary-700">
                        Få en faglig gennemgang af dine ønsker og et uforpligtende tilbud på opgaven.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <a
                        href="#kontakt-sektion"
                        className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2.5 text-xs font-bold text-white transition-colors shadow-xs"
                      >
                        Få et tilbud
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                      <a
                        href={`tel:${COMPANY_INFO.phoneRaw}`}
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-3.5 py-2.5 text-xs font-semibold text-primary-900 transition-colors"
                      >
                        <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Ring 24 26 30 12
                      </a>
                    </div>
                  </div>

                  {/* Showcase image for ombygning */}
                  <figure className="my-6 overflow-hidden rounded-2xl border border-primary-200/80 bg-white shadow-xs">
                    <img
                      src={getOptimizedImageUrl(
                        "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00003-3-scaled.jpeg",
                        { width: 1100, quality: 82 }
                      )}
                      alt="Ombygning og tømrerarbejde udført af Sehested Tømrerfirma"
                      className="h-64 sm:h-80 md:h-96 w-full object-cover"
                      loading="lazy"
                    />
                    <figcaption className="px-4 py-3 text-xs sm:text-sm text-primary-600 bg-primary-50/60 border-t border-primary-100 flex items-center justify-between">
                      <span>Gennemført ombygning med nye rumforløb og skræddersyet finish.</span>
                      <span className="font-semibold text-brand-700 shrink-0 ml-3">Sehested Tømrerfirma</span>
                    </figcaption>
                  </figure>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Få hjælp til ombygning af hus, før arbejdet går i gang
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      God hjælp til ombygning af hus starter med at få behov og muligheder til at mødes. Vi taler om, hvad der fungerer i boligen i dag, hvad der skal ændres, og hvilke løsninger der giver mening. Du får direkte dialog med mig uden skiftende kontaktpersoner, så aftaler og praktiske valg ikke går tabt undervejs.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Større ændringer kan kræve byggetilladelse, beregninger eller inddragelse af andre fagfolk. Det afhænger blandt andet af, om bærende konstruktioner ændres, eller om boligens areal og anvendelse påvirkes. De forhold bør afklares tidligt, så projektet ikke bygger på antagelser.
                    </p>

                    <h3 className="font-display text-lg font-bold text-primary-950 mt-4">
                      Sådan holder vi en klar retning gennem projektet
                    </h3>
                    <p className="mt-2 leading-relaxed">
                      Jeg følger tømrerarbejdet fra den første gennemgang til den færdige udførelse. Det giver dig samme fagperson at sparre med om detaljer, materialer og justeringer undervejs. Den personlige arbejdsform gør det lettere at bevare overblikket, uanset om opgaven gælder enkelte rum eller en omfattende ombygning af bolig.
                    </p>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Ombygning af 70 er hus og sommerhus med blik for helheden
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Ved ombygning af 70 er hus er der ofte gode muligheder for at gentænke planløsningen. Mange parcelhuse har en tydelig opdeling mellem køkken, stue og gangarealer. En gennemtænkt løsning kan skabe bedre sammenhæng, men konstruktioner,{" "}
                      <Link
                        to="/ydelser/tagarbejde"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        tag
                      </Link>
                      ,{" "}
                      <Link
                        to="/ydelser/doere-og-vinduer"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        vinduer
                      </Link>{" "}
                      og husets oprindelige kvaliteter skal vurderes samlet.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      En sommerhus ombygning handler ofte om at få mere ud af få kvadratmeter og skabe en enkel forbindelse mellem rummene og udelivet – eventuelt suppleret med en ny{" "}
                      <Link
                        to="/ydelser/traeterrasser"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        træterrasse
                      </Link>
                      . Her er det vigtigt at vælge materialer og konstruktioner, som passer til husets brug og stand. Jeg hjælper med den tømrermæssige udførelse og de konkrete løsninger.
                    </p>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Lokal ombygning med base i Hasselager – Aarhus, Højbjerg og Viby
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Jeg har base i Hasselager og hjælper boligejere med ombygning af hus i Hasselager, Aarhus og omegn. Søger du ombygning i{" "}
                      <Link
                        to="/toemrer-hoejbjerg"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Højbjerg
                      </Link>{" "}
                      eller ombygning i{" "}
                      <Link
                        to="/toemrer-viby"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Viby
                      </Link>
                      , får du en tømrer med base tæt på områderne og direkte kontakt gennem opgaven. Jeg kører også til{" "}
                      <Link
                        to="/toemrer-maarslet"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Mårslet
                      </Link>
                      ,{" "}
                      <Link
                        to="/toemrer-skanderborg"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Skanderborg
                      </Link>{" "}
                      og{" "}
                      <Link
                        to="/toemrer-odder"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Odder
                      </Link>
                      . Hvis projektet også kræver flere kvadratmeter, kan vi også tale om en{" "}
                      <Link
                        to="/ydelser/tilbygninger"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        tilbygning
                      </Link>{" "}
                      eller styring som{" "}
                      <Link
                        to="/totalentreprise"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        totalentreprise
                      </Link>
                      .
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Lokalt håndværk handler for mig ikke om store ord, men om at være til at tale med og tage ansvar for mit eget arbejde. Hos Sehested Tømrerfirma er det mig, du møder, både når løsningen skal drøftes, og når værktøjet kommer frem. Det giver en enkel kontakt og færre muligheder for misforståelser.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-display text-lg font-bold text-primary-950">
                      Hvad koster en ombygning af hus?
                    </h3>
                    <p className="mt-2 leading-relaxed">
                      Prisen på en ombygning af hus afhænger af omfanget, materialerne og de eksisterende konstruktioner. Det har også betydning, om vægge, tag, vinduer eller installationer bliver berørt, og om andre fag skal med. Derfor giver en konkret gennemgang et bedre grundlag end en generel pris pr. kvadratmeter.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Har du brug for hjælp til ombygning af hus, er du velkommen til at kontakte mig direkte på tlf.{" "}
                      <a
                        href="tel:24263012"
                        className="font-bold text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        24 26 30 12
                      </a>{" "}
                      eller mail{" "}
                      <a
                        href="mailto:ssj@ssehested.dk"
                        className="font-bold text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        ssj@ssehested.dk
                      </a>
                      . Sammen kan vi afklare, hvilke dele der skal ændres, og hvordan tømrerarbejdet kan gribes an. Du får en direkte og faglig dialog med den samme tømrer gennem hele forløbet.
                    </p>
                  </div>

                  {/* Mid-content CTA block 2 */}
                  <div className="rounded-xl border border-primary-200 bg-primary-50/80 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-base font-bold text-primary-950">
                        Klar til at skabe en bedre rumfordeling i boligen?
                      </p>
                      <p className="mt-0.5 text-xs sm:text-sm text-primary-700">
                        Ring direkte på 24 26 30 12 eller udfyld kontaktformularen.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <a
                        href="tel:24263012"
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-100/70 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                      >
                        <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Ring 24 26 30 12
                      </a>
                      <a
                        href="#kontakt-sektion"
                        className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2 text-xs font-bold text-white transition-colors shadow-xs"
                      >
                        Få et tilbud
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              ) : slug === "totalentrepriser" || slug === "totalentreprise" ? (
                <div className="space-y-5 text-primary-800">
                  <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-primary-950 leading-tight">
                    Totalentreprise i Hasselager & Aarhus med én fast tømrer tæt på forløbet
                  </h1>

                  <p className="text-base sm:text-lg leading-relaxed text-primary-700">
                    Skal du renovere eller bygge til, kan en totalentreprise give en mere overskuelig vej fra idé til færdigt byggeri. Hos{" "}
                    <Link
                      to="/"
                      className="font-semibold text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                    >
                      Sehested Tømrerfirma
                    </Link>{" "}
                    møder du mig, Søren Sehested. Jeg har base i Hasselager, over 20 års erfaring og står selv med værktøjet, når tømrerarbejdet udføres.
                  </p>

                  <div className="flex flex-wrap items-center gap-2.5">
                    <a
                      href="tel:24263012"
                      className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                    >
                      <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                      Ring 24 26 30 12
                    </a>
                    <a
                      href="mailto:ssj@ssehested.dk"
                      className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                    >
                      <Mail className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                      Send mail
                    </a>
                    <a
                      href="#kontakt-sektion"
                      className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2 text-xs font-bold text-white transition-colors shadow-xs"
                    >
                      Få et tilbud
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </a>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Hvad er en totalentreprise?
                    </h2>
                    <p className="mt-3 leading-relaxed">
                      En totalentreprise er en entrepriseform, hvor projektering og udførelse som udgangspunkt samles i én aftale med en totalentreprenør. Det kan mindske antallet af overdragelser for dig som boligejer. Den præcise aftale afhænger dog altid af projektet, og derfor skal omfanget af arbejdet afklares grundigt.
                    </p>
                    <p className="mt-3 leading-relaxed">
                      Totalentrepriser er især relevante, når flere dele af boligen skal tænkes sammen. Det kan være en{" "}
                      <Link
                        to="/ydelser/tilbygninger"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        tilbygning
                      </Link>{" "}
                      som totalentreprise, en totalentreprise ved{" "}
                      <Link
                        to="/totalrenovering"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        renovering
                      </Link>{" "}
                      eller en større{" "}
                      <Link
                        to="/ombygning"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        ombygning
                      </Link>{" "}
                      med omfattende tømrerarbejde. Målet er at skabe et sammenhængende projekt med tydelige rammer fra begyndelsen.
                    </p>
                    <ul className="mt-4 space-y-2 list-disc pl-5 text-primary-700">
                      <li>
                        En tilbygning som totalentreprise, hvor de nye kvadratmeter skal passe til boligens konstruktion, materialer og eksisterende udtryk.
                      </li>
                      <li>
                        Totalentreprise ved renovering, hvor flere rum, konstruktioner eller bygningsdele skal fornyes som en samlet løsning.
                      </li>
                      <li>
                        En ombygning med eksempelvis nye rumforløb, tagarbejde, døre, vinduer og andre opgaver, der kræver erfaren tømrerfaglig indsigt.
                      </li>
                    </ul>
                  </div>

                  {/* Mid-content CTA block 1 */}
                  <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-base font-bold text-primary-950">
                        Har du et byggeprojekt eller en større renovering i tankerne?
                      </p>
                      <p className="mt-1 text-xs sm:text-sm text-primary-700">
                        Få en grundig gennemgang af mulighederne og et uforpligtende tilbud på totalentreprise.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <a
                        href="#kontakt-sektion"
                        className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2.5 text-xs font-bold text-white transition-colors shadow-xs"
                      >
                        Få et tilbud
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                      <a
                        href={`tel:${COMPANY_INFO.phoneRaw}`}
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-3.5 py-2.5 text-xs font-semibold text-primary-900 transition-colors"
                      >
                        <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Ring 24 26 30 12
                      </a>
                    </div>
                  </div>

                  {/* Showcase image for totalentreprise */}
                  <figure className="my-6 overflow-hidden rounded-2xl border border-primary-200/80 bg-white shadow-xs">
                    <img
                      src={getOptimizedImageUrl(
                        "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00006-2-scaled.jpeg",
                        { width: 1100, quality: 82 }
                      )}
                      alt="Totalentreprise med tilbygning og tømrerarbejde udført af Sehested Tømrerfirma"
                      className="h-64 sm:h-80 md:h-96 w-full object-cover"
                      loading="lazy"
                    />
                    <figcaption className="px-4 py-3 text-xs sm:text-sm text-primary-600 bg-primary-50/60 border-t border-primary-100 flex items-center justify-between">
                      <span>Totalentreprise samler planlægning og udførelse med én fast tømrer på pladsen.</span>
                      <span className="font-semibold text-brand-700 shrink-0 ml-3">Sehested Tømrerfirma</span>
                    </figcaption>
                  </figure>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Totalentreprise eller hovedentreprise, hvad skal du vælge?
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Når du sammenligner totalentreprise og hovedentreprise, ligger forskellen især i projekteringen. Ved en hovedentreprise er projektmaterialet normalt udarbejdet, før hovedentreprenøren tager ansvar for udførelsen. Ved en totalentreprise omfatter aftalen som udgangspunkt både projektering og udførelse.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Valget mellem totalentreprise og hovedentreprise afhænger blandt andet af, hvor langt du er med dine planer. Søger du en tømrer til totalentreprise, er det vigtigt at få aftalegrænserne gjort tydelige. Behov for andre fag, tegninger, beregninger og myndighedsafklaring skal beskrives konkret i den enkelte aftale.
                    </p>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Sådan får din totalentreprise en god begyndelse
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Et godt byggeforløb starter med en grundig samtale om boligen, dine ønsker og de praktiske muligheder. Vi ser på, hvad der skal ændres, hvad der eventuelt kan bevares, og hvilke dele af projektet der kræver særlig opmærksomhed. Det giver et bedre grundlag for både løsningen og den videre dialog.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-display text-lg sm:text-xl font-bold text-primary-950">
                      Først gør vi dine ønsker konkrete
                    </h3>
                    <p className="mt-2 leading-relaxed">
                      Det er en fordel at overveje funktion, materialer og prioriteringer, inden arbejdet begynder. Ved en totalentreprise i forbindelse med renovering kan beslutninger ét sted påvirke resten af boligen. Derfor taler vi om sammenhængen mellem konstruktioner, udtryk og den måde, du ønsker at bruge rummene på.
                    </p>
                    <ul className="mt-3 space-y-2 list-disc pl-5 text-primary-700">
                      <li>Hvilke dele af boligen skal ændres, og hvilke eksisterende løsninger ønsker du at bevare.</li>
                      <li>Hvilke materialer, funktioner og visuelle ønsker er vigtigst for dig og din hverdag.</li>
                      <li>Om der allerede findes tegninger, tilladelser eller aftaler med rådgivere og andre håndværksfag.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-display text-lg sm:text-xl font-bold text-primary-950">
                      Derefter afklarer vi omfang og økonomi
                    </h3>
                    <p className="mt-2 leading-relaxed">
                      Prisen på en totalentreprise afhænger blandt andet af projektets størrelse, boligens stand, materialevalg og behovet for forskellige fag. En realistisk økonomi kræver derfor, at opgaven først bliver gjort konkret. Når rammerne er tydelige, er det nemmere at prioritere og beskrive den ønskede løsning.
                    </p>
                  </div>

                  {/* Core pillars from previous content */}
                  <div className="rounded-2xl border border-primary-200 bg-primary-50/50 p-6 sm:p-7 space-y-5">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-brand-700">
                        Vores grundværdier på dit byggeri
                      </span>
                      <h3 className="font-display text-lg sm:text-xl font-bold text-primary-950 mt-1">
                        Tryghed og overblik gennem hele totalentreprisen
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-xl border border-primary-200/80 bg-white p-5 shadow-2xs space-y-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                          <CheckCircle2 className="h-5 w-5" />
                        </div>
                        <h4 className="font-display text-base font-bold text-primary-950">
                          Jeg har fingeren på det hele
                        </h4>
                        <p className="text-xs sm:text-sm text-primary-700 leading-relaxed">
                          Som tømrer følger jeg projektet tæt – fra første idé til sidste finish. Jeg holder styr på detaljerne, koordinerer undervejs og sørger for, at du hele tiden ved, hvor vi er i processen. Det giver dig tryghed og et resultat, der holder.
                        </p>
                      </div>

                      <div className="rounded-xl border border-primary-200/80 bg-white p-5 shadow-2xs space-y-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                          <Phone className="h-5 w-5" />
                        </div>
                        <h4 className="font-display text-base font-bold text-primary-950">
                          Personlig kontakt
                        </h4>
                        <p className="text-xs sm:text-sm text-primary-700 leading-relaxed">
                          Du taler direkte med tømreren – ikke en mellemmand. Vi sætter en ære i god dialog, så du altid ved, hvem du har med at gøre, og hvad der sker på opgaven.
                        </p>
                      </div>

                      <div className="rounded-xl border border-primary-200/80 bg-white p-5 shadow-2xs space-y-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                          <Award className="h-5 w-5" />
                        </div>
                        <h4 className="font-display text-base font-bold text-primary-950">
                          Høj kvalitet
                        </h4>
                        <p className="text-xs sm:text-sm text-primary-700 leading-relaxed">
                          Vi leverer solidt håndværk, der holder i mange år. Ingen smutveje – kun godt tømrerarbejde udført med præcision og faglig stolthed.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Din lokale totalentreprenør med base i Hasselager, Aarhus og omegn
                    </h2>
                    <p className="mt-2.5 leading-relaxed">
                      Leder du efter en totalentreprenør i Hasselager eller Aarhus, får du hos Sehested Tømrerfirma en lokalt forankret tømrer med base netop her i Hasselager. Jeg arbejder også i{" "}
                      <Link
                        to="/toemrer-hoejbjerg"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Højbjerg
                      </Link>
                      ,{" "}
                      <Link
                        to="/toemrer-viby"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Viby
                      </Link>
                      ,{" "}
                      <Link
                        to="/toemrer-maarslet"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Mårslet
                      </Link>
                      ,{" "}
                      <Link
                        to="/toemrer-skanderborg"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Skanderborg
                      </Link>{" "}
                      og{" "}
                      <Link
                        to="/toemrer-odder"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        Odder
                      </Link>
                      . Du har direkte kontakt med mig, og der er ikke skiftende kontaktpersoner mellem samtalen og tømrerarbejdet.
                    </p>
                    <p className="mt-2.5 leading-relaxed">
                      Fortæl mig, om du overvejer en{" "}
                      <Link
                        to="/ydelser/tilbygninger"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        tilbygning
                      </Link>
                      ,{" "}
                      <Link
                        to="/ombygning"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        ombygning
                      </Link>{" "}
                      eller totalentreprise ved{" "}
                      <Link
                        to="/totalrenovering"
                        className="font-medium text-brand-700 hover:text-brand-800 underline decoration-brand-400/60 hover:decoration-brand-700 underline-offset-2 transition-colors"
                      >
                        renovering
                      </Link>{" "}
                      af din bolig. Så kan vi tage en faglig snak om dine planer og afklare, hvad projektet kræver. Du får personlig kontakt med en erfaren tømrer, der lægger vægt på solidt håndværk og løsninger tilpasset boligen.
                    </p>
                  </div>

                  {/* Mid-content CTA block 2 */}
                  <div className="rounded-xl border border-primary-200 bg-primary-50/80 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-base font-bold text-primary-950">
                        Klar til at tage en uforpligtende snak om totalentreprise?
                      </p>
                      <p className="mt-0.5 text-xs sm:text-sm text-primary-700">
                        Ring direkte på 24 26 30 12 eller udfyld kontaktformularen.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <a
                        href="tel:24263012"
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-100/70 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                      >
                        <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Ring 24 26 30 12
                      </a>
                      <a
                        href="#kontakt-sektion"
                        className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2 text-xs font-bold text-white transition-colors shadow-xs"
                      >
                        Få et tilbud
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <article
                    className="rich-text leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />

                  {/* Mid-content CTA block for other service pages */}
                  <div className="mt-10 rounded-xl border border-brand-200 bg-brand-50/50 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-display text-base font-bold text-primary-950">
                        Skal vi gennemgå dine muligheder for {title.toLowerCase()}?
                      </h3>
                      <p className="mt-1 text-xs sm:text-sm text-primary-700">
                        Få en uforpligtende faglig vurdering og et tilbud tilpasset din bolig.
                      </p>
                    </div>
                    <a
                      href="#kontakt-sektion"
                      className="shrink-0 inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-5 py-2.5 text-xs font-bold text-white transition-colors shadow-xs"
                    >
                      Få et tilbud
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </a>
                  </div>
                </>
              )}
            </div>

            {/* Google Reviews Callout Banner for All Services */}
            <div className="overflow-hidden rounded-2xl border border-primary-200/90 bg-gradient-to-br from-white via-primary-50/40 to-amber-50/30 p-6 sm:p-8 shadow-xs">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-3 max-w-xl">
                  <div className="flex items-center gap-3">
                    {/* Official Google Multi-color G logo */}
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-xs border border-primary-100 p-2 shrink-0">
                      <svg className="h-full w-full" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                        <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                      </svg>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-primary-950">Google Anmeldelser</span>
                      </div>
                      <p className="text-xs text-primary-500 font-medium">Sehested Tømrerfirma • Hasselager & Aarhus</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Har vi hjulpet dig med et bygge- eller renoveringsprojekt?
                    </h3>
                    <p className="mt-2 text-sm text-primary-700 leading-relaxed">
                      Din tilbagemelding har stor betydning for vores arbejde. Har du fået udført {title ? title.toLowerCase() : "tømrerarbejde"} eller et byggeprojekt af Søren Sehested, sætter vi stor pris på, hvis du vil dele din oplevelse med en hurtig anmeldelse på Google.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-primary-600 pt-1">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Tager under 1 minut
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Direkte på Google
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Hjælper lokale boligejere
                    </span>
                  </div>
                </div>

                <div className="shrink-0 flex flex-col sm:flex-row md:flex-col items-stretch sm:items-center md:items-stretch gap-2.5">
                  <a
                    href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 rounded-full bg-primary-950 hover:bg-brand-600 px-6 py-3.5 text-xs sm:text-sm font-bold text-white transition-all duration-200 shadow-sm group cursor-pointer"
                  >
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                      <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                    </svg>
                    <span>Giv en Google anmeldelse</span>
                    <ExternalLink className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </a>
                  <p className="text-center text-[11px] text-primary-500">
                    Åbner direkte i Google Reviews
                  </p>
                </div>
              </div>
            </div>

            {/* In-page FAQ section if defined */}
            {faqData && faqData.items && faqData.items.length > 0 && (
              <div className="rounded-2xl border border-primary-200/80 bg-white p-6 sm:p-9 shadow-xs">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-700 mb-1">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Svar på det meste</span>
                  </div>
                  <h2 className="font-display text-2xl font-bold tracking-tight text-primary-950 sm:text-3xl">
                    {faqData.section_heading || "Ofte stillede spørgsmål"}
                  </h2>
                </div>

                <div className="divide-y divide-primary-100">
                  {faqData.items.map((faqItem, idx) => {
                    const isOpen = openFaqIndex === idx;
                    return (
                      <div key={idx} className="py-4 first:pt-0 last:pb-0">
                        <button
                          type="button"
                          onClick={() => setOpenFaqIndex(isOpen ? -1 : idx)}
                          className="flex w-full items-start justify-between text-left gap-4 group cursor-pointer"
                          aria-expanded={isOpen}
                        >
                          <h3 className="font-display text-base font-bold text-primary-950 group-hover:text-brand-700 transition-colors">
                            {faqItem.question}
                          </h3>
                          <span className={`rounded-full p-1 border border-primary-200 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180 bg-brand-50 text-brand-700" : "text-primary-500"}`}>
                            <ChevronDown className="h-4 w-4" />
                          </span>
                        </button>
                        {isOpen && (
                          <div className="mt-3 text-sm leading-relaxed text-primary-700 pr-4">
                            <p>{faqItem.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Gallery Section */}
            {gallery && gallery.images && gallery.images.length > 0 && (
              <div className="rounded-2xl border border-primary-200/80 bg-white p-6 sm:p-9 shadow-xs space-y-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-bold text-primary-950">
                      Billeder fra tidligere projekter
                    </h2>
                    <p className="text-xs text-primary-500">Se udvalgte eksempler på vores arbejde</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {gallery.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setLightboxIndex(index)}
                      className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-primary-200/60 bg-primary-100 shadow-xs hover:border-brand-500 transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <img
                        src={getOptimizedImageUrl(img, { width: 400, height: 300 })}
                        alt={`${title} galleri ${index + 1}`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-300" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Column */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
            {/* Direct Master Profile Box */}
            <div className="rounded-2xl border border-primary-200 bg-white p-6 shadow-xs">
              <div className="flex items-center gap-3 pb-4 border-b border-primary-100">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700 border border-brand-200/60 font-display font-bold text-lg">
                  SS
                </div>
                <div>
                  <h4 className="font-display font-bold text-primary-950">Søren Sehested</h4>
                  <p className="text-xs text-primary-500">Tømrermester & Indehaver</p>
                </div>
              </div>

              <div className="mt-5 space-y-4 text-sm">
                <p className="text-primary-700 leading-relaxed text-xs">
                  Hos Sehested Tømrerfirma er det mig personligt, du taler med, og mig, der står med værktøjet på din opgave.
                </p>

                <div className="space-y-2 pt-2">
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="flex items-center justify-center gap-2.5 w-full rounded-full bg-brand-400 hover:bg-brand-300 px-4 py-3 text-xs font-bold uppercase tracking-wider text-primary-950 transition-colors shadow-xs"
                  >
                    <Phone className="h-4 w-4" />
                    <span>Ring {COMPANY_INFO.phoneDisplay}</span>
                  </a>
                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="flex items-center justify-center gap-2.5 w-full rounded-full border border-primary-200 hover:bg-primary-50 px-4 py-3 text-xs font-semibold text-primary-800 transition-colors"
                  >
                    <Mail className="h-4 w-4 text-primary-500" />
                    <span>{COMPANY_INFO.email}</span>
                  </a>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-primary-100 space-y-2.5 text-xs text-primary-600">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-brand-600 shrink-0" />
                  <span>20+ års erfaring som tømrer</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-600 shrink-0" />
                  <span>Dækket af Byg Garanti op til 50.000 kr.</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-brand-600 shrink-0" />
                  <span>Hurtig tilbagemelding på alle henvendelser</span>
                </div>
              </div>
            </div>

            {/* Google Reviews Badge for Sidebar */}
            <div className="rounded-2xl border border-primary-200 bg-white p-5 shadow-xs text-center space-y-3">
              <div className="flex items-center justify-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 border border-primary-200/60 p-1.5 shadow-2xs shrink-0">
                  <svg className="h-full w-full" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                    <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-primary-950">Google Anmeldelser</p>
                  <p className="text-[11px] text-primary-500 font-medium">Sehested Tømrerfirma</p>
                </div>
              </div>

              <p className="text-xs text-primary-600 leading-relaxed">
                Har du samarbejdet med Sehested Tømrerfirma? Vi værdsætter din bedømmelse højt.
              </p>

              <a
                href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 w-full rounded-full border border-primary-300 bg-primary-50 hover:bg-brand-600 hover:text-white hover:border-brand-600 px-3.5 py-2.5 text-xs font-bold text-primary-900 transition-all shadow-xs group"
              >
                <span>Giv en bedømmelse</span>
                <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
              </a>
            </div>

            {/* Other Services Navigation Box */}
            <div className="rounded-2xl border border-primary-200 bg-white p-6 shadow-xs">
              <h4 className="font-display text-sm font-bold uppercase tracking-wider text-primary-900 mb-3">
                Vores øvrige tømrerydelser
              </h4>
              <div className="space-y-1 text-xs">
                {[
                  { title: "Totalrenovering", path: "/totalrenovering", slug: "totalrenoveringer" },
                  { title: "Ombygning", path: "/ombygning", slug: "ombygninger" },
                  { title: "Totalentreprise", path: "/totalentreprise", slug: "totalentrepriser" },
                  { title: "Tilbygninger", path: "/ydelser/tilbygninger", slug: "tilbygninger" },
                  { title: "Træterrasser", path: "/ydelser/traeterrasser", slug: "traeterrasser" },
                  { title: "Døre og vinduer", path: "/ydelser/doere-og-vinduer", slug: "doere-og-vinduer" },
                  { title: "Tagarbejde", path: "/ydelser/tagarbejde", slug: "tagarbejde" }
                ].filter(s => s.slug !== slug && s.path !== `/${rawSlug}`).map((srv, i) => (
                  <Link
                    key={i}
                    to={srv.path}
                    className="flex items-center justify-between py-2 px-2.5 rounded-lg text-primary-700 hover:bg-primary-50 hover:text-brand-700 font-medium transition-colors"
                  >
                    <span>{srv.title}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-primary-400" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Local Coverage Links */}
            <div className="rounded-2xl border border-primary-200 bg-white p-6 shadow-xs">
              <h4 className="font-display text-sm font-bold uppercase tracking-wider text-primary-900 mb-2">
                Lokal tømrer i området
              </h4>
              <p className="text-xs text-primary-600 mb-3">
                Vi har base i Hasselager og udfører tømrerarbejde i hele lokalområdet:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { name: "Højbjerg", path: "/toemrer-hoejbjerg" },
                  { name: "Viby J", path: "/toemrer-viby" },
                  { name: "Mårslet", path: "/toemrer-maarslet" },
                  { name: "Skanderborg", path: "/toemrer-skanderborg" },
                  { name: "Odder", path: "/toemrer-odder" },
                  { name: "Aarhus Syd", path: "/kontakt" }
                ].map((loc, i) => (
                  <Link
                    key={i}
                    to={loc.path}
                    className="inline-block rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs text-primary-800 hover:border-brand-500 hover:text-brand-700 transition-colors"
                  >
                    {loc.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* Bottom Full-Width Contact Form & Info Section */}
        <section id="kontakt-sektion" className="mt-16 pt-12 border-t border-primary-200 scroll-mt-20">
          <div className="mb-10 text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-600">Kontakt Sehested Tømrerfirma</span>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-primary-950 sm:text-4xl">
              Få et uforpligtende tilbud på dit projekt
            </h2>
            <p className="mt-3 text-sm sm:text-base text-primary-600 leading-relaxed">
              Beskriv gerne din bolig, dine ønsker og projektets omfang. Så vender jeg hurtigt tilbage med en faglig snak om mulighederne og en konkret prisvurdering.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Form Column */}
            <div className="lg:col-span-8">
              <ContactForm
                title={`Kontakt Søren Sehested vedr. ${title.toLowerCase()}`}
                subtitle="Udfyld formularen herunder – jeg besvarer alle henvendelser hurtigt og personligt."
              />
            </div>

            {/* Contact Details Card in Bottom */}
            <div className="lg:col-span-4 space-y-6">
              <div className="rounded-2xl bg-primary-950 text-white p-6 sm:p-8 shadow-sm border border-primary-800 space-y-6">
                <h3 className="font-display text-xl font-bold text-white">
                  Direkte kontaktinformation
                </h3>
                <p className="text-primary-300 text-xs sm:text-sm leading-relaxed">
                  Foretrækker du at ringe eller sende en direkte e-mail, er du altid velkommen til at kontakte mig direkte:
                </p>

                <div className="space-y-4">
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="flex items-center gap-3.5 p-3.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-brand-400 transition-colors"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/20 text-brand-300 shrink-0">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-400">Ring direkte</p>
                      <p className="text-sm font-bold text-white">Ring {COMPANY_INFO.phoneDisplay}</p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="flex items-center gap-3.5 p-3.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-brand-400 transition-colors"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/20 text-brand-300 shrink-0">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-400">Send en e-mail</p>
                      <p className="text-sm font-bold text-white">{COMPANY_INFO.email}</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/20 text-brand-300 shrink-0">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-400">Adresse & Base</p>
                      <p className="text-xs font-semibold text-white">{COMPANY_INFO.address}</p>
                      <p className="text-[11px] text-primary-300">CVR: {COMPANY_INFO.cvr}</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10 text-xs text-primary-300 space-y-1.5">
                  <p className="font-semibold text-white">Åbningstider:</p>
                  <p>{COMPANY_INFO.hours}</p>
                  <p className="text-[11px] text-brand-300 pt-1">Hurtig respons på både opkald og henvendelser.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Lightbox for Gallery */}
      {gallery && gallery.images && (
        <Lightbox
          images={gallery.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length)}
          onNext={() => setLightboxIndex((prev) => (prev + 1) % gallery.images.length)}
        />
      )}
    </div>
  );
}
